<template>
  <BaseDropdownWrapper
    :with-full="props.withFull"
    class="base-autocomplete"
    :class="computedClass"
    :bg-color="props.bgColor"
    :with-rounded="props.withRounded"
    :offset-y="props.offsetY"
    :close-on-control-click="false"
    :off="props.readonly"
  >
    <template #control>
      <BaseInput
        :model-value="searchText"
        with-full
        :variant="props.variant"
        :placeholder="props.placeholder"
        :disabled="props.disabled"
        :readonly="props.readonly"
        :with-clear="props.withClear"
        :with-shadow="props.withShadow"
        @update:model-value="handleSearch($event as string)"
        @focus="handleFocus"
      >
        <template #prepend-icon>
          <slot name="prepend-icon" />
        </template>
        <template #append-icon>
          <slot name="append-icon" />
        </template>
      </BaseInput>
    </template>
    <template #dropdown="{ handleClose }">
      <ul
        v-if="props.options.length && !hasLoading"
        class="base-autocomplete__content"
      >
        <li
          v-for="(item, index) in props.options"
          :key="index"
          class="base-autocomplete__item"
          @click="handleSelectItem(item, handleClose)"
        >
          <slot name="item" :data="item" :selected="selectedOption">
            {{
              props.optionLabel ? interpolate(item, props.optionLabel) : item
            }}
          </slot>
        </li>
      </ul>
      <ul v-else class="base-dropdown-autocomplete__content">
        <li
          v-if="hasLoading"
          class="base-autocomplete__item base-autocomplete__item--loader"
        >
          <BaseLoader size="20px" color="var(--color-foreground-primary)" />
        </li>
        <li
          v-else-if="searchText && !props.options.length"
          class="base-autocomplete__item base-autocomplete__item--not-found"
        >
          <slot name="empty-data">
            Данные не найдены
          </slot>
        </li>
        <li
          v-else-if="!searchText && !props.options.length"
          class="base-autocomplete__item base-autocomplete__item--not-found"
        >
          <slot name="empty-init">
            Данные не найдены
          </slot>
        </li>
      </ul>
    </template>
  </BaseDropdownWrapper>
</template>

<script
  setup
  lang="ts"
  generic="T extends Record<string, any>, K extends keyof T = keyof T"
>
import getByChain from 'lodash/get'
import BaseDropdownWrapper from '../dropdown-wrapper/index.vue'
import BaseInput from '../input/index.vue'
import BaseLoader from '../loader/index.vue'

interface Props<T> {
  options: Array<T>
  optionLabel?: string
  optionValue?: string
  variant?: 'light' | 'secondary'
  readonly?: boolean
  withFull?: boolean
  placeholder?: string
  disabled?: boolean
  bgColor?: string
  withRounded?: boolean
  withImmediateSelect?: boolean
  offsetY?: number
  withClear?: boolean
  withShadow?: boolean
  minNumberCharacters?: number
}

const props = withDefaults(defineProps<Props<T>>(), {
  optionLabel: '{value}',
  optionValue: '{value}',
  disabled: false,
  minNumberCharacters: 0,
})

const emit = defineEmits<{
  (e: 'search', value: string): void
  (e: 'select', value: T | T[K] | null): void
}>()
const selectedOption = defineModel<T | T[K] | null>({ required: true })

const DELAY_OF_TYPING = 300
const timeout = ref<ReturnType<typeof setTimeout>>()
const searchText = ref('')
const hasLoading = ref(false)

const computedClass = computed(() => ({
  [`base-autocomplete--variant-${props.variant}`]: props.variant,
}))

function interpolate(
  data: Record<string, any> | string,
  template: string,
): string {
  const parts: string[] = []

  template.replace(/\{([\w.$]+)\}/g, (_, path) => {
    const value = getByChain(data, path)
    if (value !== undefined && value !== null && value !== '') {
      parts.push(String(value).trim())
    }
    return ''
  })

  return parts.join(' ')
}

function handleSelectItem(item: T, handleClose: () => void) {
  searchText.value = String(
    props.optionLabel ? interpolate(item, props.optionLabel) : item,
  )
  selectedOption.value = props.optionValue
    ? (interpolate(item, props.optionValue) as T[K])
    : item
  emit('select', item)
  handleClose()
}

function handleSearch(value: string) {
  searchText.value = value

  if (props.withImmediateSelect) {
    selectedOption.value = value as T[K]
  }

  if (value === '') {
    hasLoading.value = false
    clearTimeout(timeout.value)
    selectedOption.value = null
    emit('search', '')
    emit('select', null)
    return
  }

  if (value.length < props.minNumberCharacters) {
    return
  }

  hasLoading.value = true

  clearTimeout(timeout.value)
  timeout.value = setTimeout(() => {
    emit('search', value)
  }, DELAY_OF_TYPING)
}

function handleFocus() {
  if (selectedOption.value === null) {
    return
  }
  handleSearch(searchText.value)
}

watch(
  () => props.options,
  () => {
    hasLoading.value = false
  },
  { deep: true },
)

function syncSearchTextFromModel() {
  const value = selectedOption.value
  if (value === null || value === undefined) {
    searchText.value = ''
    return
  }
  if (typeof value !== 'string') {
    return
  }
  const foundOption = props.options.find(
    option => String(interpolate(option, props.optionValue)) === String(value),
  )
  if (props.optionLabel && foundOption) {
    searchText.value = interpolate(foundOption, props.optionLabel)
    return
  }
  if (!foundOption && props.options.length === 0) {
    searchText.value = value
    return
  }
  searchText.value = value
}

watch(
  [() => props.options, selectedOption],
  syncSearchTextFromModel,
  { immediate: true, deep: true },
)
</script>

<style lang="scss" scoped>
.base-autocomplete {
  &__content {
    list-style-type: none;
    margin: 0;
    padding: 0;
    text-align: left;
  }

  &__item {
    @include typo-text-secondary;

    cursor: pointer;
    border-radius: var(--radius-xxs);
    padding: rem(8px) rem(10px);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover:not(&--not-found, &--loader) {
      background: var(--color-surface-bg) !important;
      color: var(--color-foreground-primary);
    }

    &--loader,
    &--not-found {
      display: flex;
      justify-content: center;
    }

    &--not-found {
      color: var(--color-foreground-regular);
      font-weight: 500;
      white-space: unset;
      margin-left: rem(-40px);
    }
  }

  &:deep(.base-dropdown-wrapper__content) {
    padding: rem(10px);
    box-shadow: 0 5px 20px -5px rgb(0 0 0 / 10%);
    border-radius: var(--radius-xs);
    border: rem(1px) solid var(--color-surface-border);
  }
}
</style>
